import { useQuery } from '@tanstack/react-query'
import { Briefcase, LogOut, Package, ShoppingCart, User } from 'lucide-react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/store'
import { getStorefrontSettings } from '@/features/marketplace/api'
import { cartCount, useCart } from '@/features/marketplace/cart'
import { cn } from '@/lib/cn'

/**
 * The public storefront's shell (M24-02).
 *
 * Deliberately not `AppShell`: there is no sidebar, no signed-in user *required*, nothing
 * that assumes `RequireAuth` ran first -- this sits outside it in the router (`main.tsx`).
 * It reuses the ERP's own light theme (the `brand`/slate Tailwind tokens, the same
 * border-and-card conventions as `Products.tsx`) rather than `LandingPage.tsx`'s dark
 * marketing skin, per the client's instruction to keep the storefront looking like the
 * product it is selling.
 *
 * The header reads the auth store but never gates on it. A shopper browses and fills a
 * basket anonymously and is asked to sign in exactly once, at checkout -- pushing the
 * account wall any earlier is what empties baskets.
 */
export function StorefrontLayout() {
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const signOut = useAuth((s) => s.signOut)
  const navigate = useNavigate()
  const count = useCart((s) => cartCount(s.lines))

  // The shop's own name, so the header does not say "Store" on a deployment that has a
  // company name configured. Long stale time: it changes about once a year.
  const { data: settings } = useQuery({
    queryKey: ['storefront-settings'],
    queryFn: getStorefrontSettings,
    retry: false,
    staleTime: 10 * 60_000,
  })

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors',
      isActive
        ? 'text-slate-900 dark:text-slate-100'
        : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100',
    )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4">
          <Link
            to="/store"
            className="truncate text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100"
          >
            {settings?.store_name ?? 'Store'}
          </Link>

          <nav className="flex items-center gap-1">
            <NavLink to="/store/careers" className={linkClass}>
              <Briefcase className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Careers</span>
            </NavLink>

            <NavLink to="/store/cart" className={linkClass}>
              <span className="relative">
                <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                {count > 0 && (
                  <span
                    className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white"
                    aria-hidden="true"
                  >
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </span>
              <span className="hidden sm:inline">Basket{count > 0 ? ` (${count})` : ''}</span>
            </NavLink>

            {status === 'authenticated' ? (
              <>
                <NavLink to="/store/orders" className={linkClass}>
                  <Package className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">My orders</span>
                </NavLink>
                <span
                  className="hidden max-w-40 truncate px-2 text-sm text-slate-500 md:inline dark:text-slate-400"
                  title={user?.email}
                >
                  {user?.full_name}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await signOut()
                    // Back to the shop, not to `/login`: signing out of a storefront means
                    // "stop being me", not "leave".
                    navigate('/store')
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            ) : (
              <NavLink to="/store/login" className={linkClass}>
                <User className="h-4 w-4" aria-hidden="true" />
                <span>Sign in</span>
              </NavLink>
            )}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-6xl px-4 pb-8 pt-4">
        <p className="border-t border-slate-200 pt-4 text-xs text-slate-400 dark:border-slate-800">
          {settings?.store_name ?? 'Store'} · Prices in {settings?.currency ?? 'INR'} and inclusive
          of tax where shown.
        </p>
      </footer>
    </div>
  )
}
